import React, { useEffect, useState, useCallback, useRef } from 'react';
import { PersistentTooltip } from './Tooltip';
import ReactPlayer from 'react-player';
import { TooltipIcon } from './icons/TooltipIcon';
import { useTrackActivity } from '../hooks/track';

// Define the keyframes for the pulse animation with a spring effect
export const pulseAnimation = `
  @keyframes pulse-spring {
    0% {
      transform: scale(1);
      
    }
    20% {
      transform: scale(1.35);
     
    }
    35% {
      transform: scale(0.95);
      
    }
    50% {
      transform: scale(1.15);
      
    }
    65% {
      transform: scale(0.98);
      
    } 
    80% {
      transform: scale(1.11);
      
    }
    100% {
      transform: scale(1);
      
    }
  }
 
  .pulse-icon { 
    animation: pulse-spring 2.5s cubic-bezier(0.28, 0.84, 0.42, 1) infinite;
    display: inline-flex;
    transform-origin: center;
    will-change: transform;
    position: relative;
    top: 2px;
  }
`;

const RecordingTooltip = ({
  hasCompletedRecording,
  contentKitStatus,
  children,
  position = 'leftTop',
}) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [playing, setPlaying] = useState(false);
  const playerRef = useRef(null);
  const track = useTrackActivity('RecordingTooltip');

  useEffect(() => {
    // Show tooltip if they haven't completed recording AND the content kit isn't being generated
    const contentKitReady =
      contentKitStatus === 'COMPLETE' || !contentKitStatus;

    setShouldShow(!hasCompletedRecording && contentKitReady);

    // Track when tooltip is shown or hidden
    if (!hasCompletedRecording && contentKitReady) {
      track('Tooltip Shown', { contentKitStatus });
    }
  }, [hasCompletedRecording, contentKitStatus, track]);

  // Stop event propagation to prevent parent elements' onClick handlers from firing
  const stopPropagation = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  // Toggle play/pause while also stopping propagation
  const togglePlay = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      setPlaying((prev) => {
        const newState = !prev;
        track('Video Playback', { action: newState ? 'play' : 'pause' });
        return newState;
      });
    },
    [track]
  );

  // Add click handler to children to clear dismissal state
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        onClick: (e) => {
          // Preserve original onClick if it exists
          if (child.props.onClick) {
            child.props.onClick(e);
          }
        },
      });
    }
    return child;
  });

  return (
    <>
      <style>{pulseAnimation}</style>
      <PersistentTooltip
        id="recording-guide"
        className="!w-[500px] !bg-[#101920] !p-8 !rounded-xl"
        buttonClassName={
          position === 'leftTop'
            ? '-translate-x-[150%] -translate-y-[125%]'
            : ''
        }
        content={
          <div
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            onTouchStart={stopPropagation}
            className="flex flex-col gap-2"
          >
            <div className="font-roboto text-white flex gap-2 items-center text-lg font-semibold">
              <div className="relative inline-flex items-center">
                <div className="pulse-icon">
                  <TooltipIcon className="relative" />
                </div>
              </div>{' '}
              Start Recording Your First Session
            </div>
            <div className="h-[0.5px] bg-[#676669] w-full" />
            <p className="font-roboto text-sm">
              Here's a quick tour of the recording studio.
            </p>
            <div
              className="relative cursor-pointer"
              onClick={(e) => {
                stopPropagation(e);
                togglePlay(e);
              }}
            >
              <ReactPlayer
                ref={playerRef}
                controls
                playing={playing}
                width="100%"
                height="auto"
                style={{
                  aspectRatio: '16:9',
                  pointerEvents: 'auto',
                }}
                url="https://storage.googleapis.com/zync-media/zync-media/manual-uploads/NavigatingRecordingStudio.mp4"
                onPause={() => {
                  setPlaying(false);
                  track('Video Paused');
                }}
                onPlay={() => {
                  setPlaying(true);
                  track('Video Played');
                }}
                onEnded={() => {
                  track('Video Completed');
                }}
                onProgress={({ played }) => {
                  if (played === 0.25)
                    track('Video Progress', { milestone: '25%' });
                  if (played === 0.5)
                    track('Video Progress', { milestone: '50%' });
                  if (played === 0.75)
                    track('Video Progress', { milestone: '75%' });
                }}
                onClick={stopPropagation}
              />
              {!playing && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePlay(e);
                    track('Play Button Clicked');
                  }}
                  className="absolute z-10 inset-0 flex items-center justify-center bg-black bg-opacity-40 cursor-pointer"
                >
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="white"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        }
        position={position}
        show={shouldShow}
      >
        {childrenWithProps}
      </PersistentTooltip>
    </>
  );
};

export default RecordingTooltip;
